import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  LocalOffer as LocalOfferIcon,
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Email as EmailIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { DiscountCode, DiscountType, DiscountScope, Player } from "../types";

interface TournamentOption {
  id: string;
  name: string;
}

interface SeasonOption {
  id: string;
  name: string;
}

const DiscountCodes: React.FC = () => {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [tournaments, setTournaments] = useState<TournamentOption[]>([]);
  const [seasons, setSeasons] = useState<SeasonOption[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [discountType, setDiscountType] = useState<DiscountType>("percentage");
  const [discountValue, setDiscountValue] = useState("");
  const [scope, setScope] = useState<DiscountScope>("tournament");
  const [target, setTarget] = useState<TournamentOption | SeasonOption | "any">("any");
  const [assignedPlayer, setAssignedPlayer] = useState<Player | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    fetchCodes();
    fetchOptions();
  }, []);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const response = await apiRequest("/api/discount-codes");
      if (!response.ok) throw new Error("Failed to load discount codes");
      const data = await response.json();
      setCodes(data);
    } catch (err) {
      setError("Failed to load discount codes");
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const [tournamentsRes, seasonsRes, playersRes] = await Promise.all([
        apiRequest("/api/tournaments"),
        apiRequest("/api/seasons"),
        apiRequest("/api/players"),
      ]);
      if (tournamentsRes.ok) setTournaments(await tournamentsRes.json());
      if (seasonsRes.ok) setSeasons(await seasonsRes.json());
      if (playersRes.ok) setPlayers(await playersRes.json());
    } catch (err) {
      console.error("Failed to load tournaments/seasons/players", err);
    }
  };

  const resetCreateForm = () => {
    setDiscountType("percentage");
    setDiscountValue("");
    setScope("tournament");
    setTarget("any");
    setAssignedPlayer(null);
    setNote("");
  };

  const handleCreate = async () => {
    setError("");
    const value = parseFloat(discountValue);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter a valid discount value");
      return;
    }
    if (discountType === "percentage" && value > 100) {
      setError("Percentage discount cannot exceed 100");
      return;
    }

    const scopeTargetId = target === "any" ? "any" : target.id;
    const scopeTargetName = target === "any" ? undefined : target.name;

    try {
      setCreating(true);
      const response = await apiRequest("/api/discount-codes", {
        method: "POST",
        body: JSON.stringify({
          discountType,
          discountValue: value,
          scope,
          scopeTargetId,
          scopeTargetName,
          assignedPlayerId: assignedPlayer?.id,
          note: note.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to create discount code");
      }

      setSuccess("Discount code created" + (assignedPlayer ? " and emailed to the player" : ""));
      setCreateOpen(false);
      resetCreateForm();
      fetchCodes();
    } catch (err: any) {
      setError(err.message || "Failed to create discount code");
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setSuccess(`Copied "${code}" to clipboard`);
  };

  const handleResend = async (id: string) => {
    setError("");
    try {
      const response = await apiRequest(`/api/discount-codes/${id}/resend`, { method: "POST" });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to resend email");
      }
      setSuccess("Email resent");
      fetchCodes();
    } catch (err: any) {
      setError(err.message || "Failed to resend email");
    }
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm("Revoke this code? It will no longer be usable.")) return;
    setError("");
    try {
      const response = await apiRequest(`/api/discount-codes/${id}/revoke`, { method: "POST" });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to revoke code");
      }
      fetchCodes();
    } catch (err: any) {
      setError(err.message || "Failed to revoke code");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this code permanently?")) return;
    setError("");
    try {
      const response = await apiRequest(`/api/discount-codes/${id}`, { method: "DELETE" });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to delete code");
      }
      fetchCodes();
    } catch (err: any) {
      setError(err.message || "Failed to delete code");
    }
  };

  const targetOptions: (TournamentOption | SeasonOption)[] = scope === "tournament" ? tournaments : seasons;

  const formatDiscount = (code: DiscountCode) =>
    code.discountType === "percentage" ? `${code.discountValue}%` : `EUR ${code.discountValue}`;

  const formatScope = (code: DiscountCode) => {
    const label = code.scope === "tournament" ? "Tournament" : "Season";
    if (code.scopeTargetId === "any") return `Any ${label}`;
    return `${label}: ${code.scopeTargetName || code.scopeTargetId}`;
  };

  const statusColor = (status: DiscountCode["status"]) =>
    status === "active" ? "success" : status === "used" ? "default" : "error";

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <LocalOfferIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h4" component="h1">Discount Codes</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Code
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Paper>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : codes.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No discount codes yet. Create one to get started.
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Applies To</TableCell>
                <TableCell>Assigned Player</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Used By</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.map((code) => (
                <TableRow key={code.id}>
                  <TableCell>
                    <Typography fontFamily="monospace" fontWeight={600}>{code.code}</Typography>
                    {code.note && (
                      <Typography variant="caption" color="text.secondary" display="block">{code.note}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDiscount(code)}</TableCell>
                  <TableCell>{formatScope(code)}</TableCell>
                  <TableCell>{code.assignedPlayerName || <Typography color="text.secondary">Unassigned</Typography>}</TableCell>
                  <TableCell>
                    <Chip label={code.status} size="small" color={statusColor(code.status) as any} />
                  </TableCell>
                  <TableCell>
                    {code.usedByPlayerName ? (
                      <Typography variant="body2">{code.usedByPlayerName}</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Copy code">
                      <IconButton size="small" onClick={() => handleCopy(code.code)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {code.assignedPlayerId && code.status === "active" && (
                      <Tooltip title="Resend email">
                        <IconButton size="small" onClick={() => handleResend(code.id)}>
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {code.status === "active" && (
                      <Tooltip title="Revoke code">
                        <IconButton size="small" color="warning" onClick={() => handleRevoke(code.id)}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {code.status !== "used" && (
                      <Tooltip title="Delete code">
                        <IconButton size="small" color="error" onClick={() => handleDelete(code.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Discount Code</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <ToggleButtonGroup
              value={discountType}
              exclusive
              onChange={(_, value) => value && setDiscountType(value)}
              fullWidth
            >
              <ToggleButton value="percentage">Percentage</ToggleButton>
              <ToggleButton value="fixed">Fixed Amount</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              label={discountType === "percentage" ? "Discount %" : "Discount Amount (EUR)"}
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Applies To</InputLabel>
              <Select
                value={scope}
                label="Applies To"
                onChange={(e) => {
                  setScope(e.target.value as DiscountScope);
                  setTarget("any");
                }}
              >
                <MenuItem value="tournament">Tournament</MenuItem>
                <MenuItem value="season">Season</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              options={[{ id: "any", name: `Any ${scope === "tournament" ? "Tournament" : "Season"}` } as TournamentOption, ...targetOptions]}
              getOptionLabel={(option) => option.name}
              value={target === "any" ? { id: "any", name: `Any ${scope === "tournament" ? "Tournament" : "Season"}` } : target}
              onChange={(_, value) => setTarget(!value || value.id === "any" ? "any" : value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label={scope === "tournament" ? "Tournament" : "Season"} />}
            />

            <Autocomplete
              options={players}
              getOptionLabel={(player) => player.name}
              value={assignedPlayer}
              onChange={(_, value) => setAssignedPlayer(value)}
              renderInput={(params) => (
                <TextField {...params} label="Assign to Player (optional)" helperText="Leave empty for a generic code anyone can redeem" />
              )}
            />

            <TextField
              label="Admin Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? "Creating..." : "Create Code"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DiscountCodes;
